import { BoardSchemeInterface, Permissions } from "ts-interfaces";
import {
  generateID,
  buildBackgroundTexture,
  DEFAULT_BACKGROUND_PRODUCT_ID,
} from "../../libs/util";
import { uploadAndSave, getDocByID } from "../../libs/firebase";
import { projectDataDefaults } from "../../MattoState";
import { fileBoard } from "../../v2/apis/accessApi";
import { createFirmFor } from "../../v2/apis/createFirm";
import { freshAccessFields } from "../../v2/apis/accessFields";

/**
 * Create a fresh (empty) board, save it, and associate it with a project.
 * Extracted from useBoardManagement.handleCreateNewBoard so the
 * "What are you starting with?" chooser (#3544 Phase 3) can reuse the exact
 * same board shape. Returns the new board so callers can update local state
 * and/or navigate to the editor.
 */
export async function createBoardInProject(
  projectId: string,
  uid: string,
  permissions: Permissions | null,
): Promise<BoardSchemeInterface> {
  // Seed from the constant defaults, NOT the live useProjectData store — reading
  // it made a fresh board inherit the last-viewed board's name/lighting/camera/
  // scene (P1-6). Clone lighting so the shared constant isn't mutated downstream.
  const projectDefaultData = {
    ...projectDataDefaults,
    lighting: projectDataDefaults.lighting
      ? { ...projectDataDefaults.lighting }
      : null,
  };
  const boardId = generateID();
  const designFirmId = await createFirmFor(uid, projectId, permissions); // stamp the workspace (Phase A.3.2)

  // Background: use the constant default if it ever gains one, else the app's
  // default product (Material-05252) — the same one /board/new applies via
  // LoadScene. Persist it here because LoadScene's fallback for a missing
  // background is broken (P1-6 fix). Fail soft on a fetch error.
  let backgroundTexture: any = projectDefaultData.backgroundTexture ?? null;
  if (!backgroundTexture) {
    try {
      const doc = await getDocByID("productsV2", DEFAULT_BACKGROUND_PRODUCT_ID);
      if (doc?.exists()) backgroundTexture = buildBackgroundTexture(doc.data());
    } catch (e) {
      console.error("Default board background fetch failed; creating without", e);
    }
  }

  const newBoard: BoardSchemeInterface = {
    id: boardId,
    name: projectDefaultData.title || "New Board",
    user: uid,
    owner: uid,
    archive: false,
    backgroundTexture,
    lighting: projectDefaultData.lighting || null,
    cameraPosition: projectDefaultData.cameraPosition || null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    projectData: {
      title: projectDefaultData.title,
      userId: uid,
      envmap: projectDefaultData.envmap,
      projectId: boardId,
      scene: projectDefaultData.scene,
      cameraMatrix: null,
    },
    physicalObjects: [],
    controlsTarget: projectDefaultData.controlsTarget || [0, 0, 0],
    design_firm_id: designFirmId,
    // Teams sharing: a new board starts private to its creator.
    ...freshAccessFields(uid),
  };

  await uploadAndSave(newBoard.id, "projects", uid, newBoard);
  // The filed board: its pointer, and under Teams sharing the project's audience.
  return { ...newBoard, ...(await fileBoard(newBoard, projectId, uid, permissions)) };
}
